import { useEffect, useState } from "react";
import { SUBSCRIPTION_LABELS } from "@dentacore/shared-types";

import styles from "./operator-page.module.css";
import { billingSummary, overdueClinics } from "./operator-api";
import type { BillingSummary, OverdueClinic } from "./operator-api";
import { priceText } from "./price-format";

/**
 * **The platform at a glance** (STORY-190b, BL-D13): the expected monthly
 * revenue — a sum of each clinic's *recorded* monthly total, nothing estimated —
 * how many clinics cannot be added up yet because a line has no price, and who is
 * past their due date. Nothing here is charged by DentaCore (BL-D1).
 */
export function OverviewPanel(): React.JSX.Element {
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [overdue, setOverdue] = useState<OverdueClinic[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void Promise.all([billingSummary(), overdueClinics()])
      .then(([s, o]) => {
        setSummary(s);
        setOverdue(o);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Could not load the overview");
      });
  }, []);

  return (
    <section className={styles.panel} data-testid="billing-overview">
      <span className={styles.panelTitle}>Overview</span>
      {error !== null && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
      {summary === null ? (
        <span className={styles.muted}>Loading…</span>
      ) : (
        <>
          <p data-testid="overview-total">
            <strong>{priceText(summary.expectedMonthlyCents)} a month</strong> expected from{" "}
            {summary.clinicsWithTotal} {summary.clinicsWithTotal === 1 ? "clinic" : "clinics"}.{" "}
            {summary.clinicsWithoutPrice > 0 &&
              `${String(summary.clinicsWithoutPrice)} more ${summary.clinicsWithoutPrice === 1 ? "has" : "have"} no price set yet and ${summary.clinicsWithoutPrice === 1 ? "is" : "are"} not counted.`}
          </p>
          <span className={styles.muted}>
            {(["ACTIVE", "GRACE", "READ_ONLY"] as const)
              .map((s) => `${SUBSCRIPTION_LABELS[s]} ${String(summary.byState[s])}`)
              .join(" · ")}
          </span>
        </>
      )}
      {overdue !== null &&
        (overdue.length === 0 ? (
          <span className={styles.muted}>Nobody is past their due date.</span>
        ) : (
          <ul className={styles.rows} data-testid="overdue-list">
            {overdue.map((c) => (
              <li key={c.clinicId} className={styles.row}>
                <span className={styles.rowName}>{c.name}</span>
                <span className={styles.rowSub}>
                  {SUBSCRIPTION_LABELS[c.state]} · due {c.dueOn} · {c.daysOverdue} days overdue
                </span>
              </li>
            ))}
          </ul>
        ))}
    </section>
  );
}
